import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import './ConvertToCanon/ConvertToCanon.css';
import { recordPageVisit } from './utils/pageVisitTracker';
import CanonEffectProcessor from './ConvertToCanon/CanonEffectProcessor';
import PresetSelector from './ConvertToCanon/PresetSelector';
import ImagePreview from './ConvertToCanon/ImagePreview';
import ProcessingIndicator from './ConvertToCanon/ProcessingIndicator';  
import PaintingModeSelector from './ConvertToCanon/PaintingModeSelector';

const SERVER_ROOT = process.env.REACT_APP_SERVER_ROOT || 'http://localhost:3001';

function ConvertToCanon({ language, user, countryCode, userIp }) {
  const [imageFile, setImageFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [outputImage, setOutputImage] = useState(null);
  const [selectedPreset, setSelectedPreset] = useState('portrait');
  const [isProcessing, setIsProcessing] = useState(false);
  const [message, setMessage] = useState('');
  const [paintingStyle, setPaintingStyle] = useState('watercolor');
  const [artStyle, setArtStyle] = useState('realism');
  const [showPaintingOptions, setShowPaintingOptions] = useState(false);
  
  const [presetOptions, setPresetOptions] = useState([
    { 
      id: 'portrait', 
      name: language === 'KR' ? '✨ 인물 모드' : '✨ Portrait Mode',
      description: language === 'KR' ? '아름다운 인물 사진으로' : 'Beautiful portrait enhancement'
    },
    { 
      id: 'landscape', 
      name: language === 'KR' ? '🌸 풍경 모드' : '🌸 Landscape Mode',
      description: language === 'KR' ? '멋진 풍경 사진으로' : 'Stunning landscape view'
    },
    { 
      id: 'macro', 
      name: language === 'KR' ? '🌺 접사 모드' : '🌺 Macro Mode',
      description: language === 'KR' ? '섬세한 디테일 촬영' : 'Detailed close-up shots'
    },
    { 
      id: 'hdr', 
      name: language === 'KR' ? '🌈 HDR 모드' : '🌈 HDR Mode',
      description: language === 'KR' ? '생생한 색감 표현' : 'Vibrant color expression'
    },
    { 
      id: 'blackwhite', 
      name: language === 'KR' ? '🖤 흑백 모드' : '🖤 Black & White Mode',
      description: language === 'KR' ? '클래식한 흑백 감성' : 'Classic monochrome style'
    },
    { 
      id: 'vintage', 
      name: language === 'KR' ? '📷 빈티지 모드' : '📷 Vintage Mode',
      description: language === 'KR' ? '따뜻한 레트로 감성' : 'Warm retro atmosphere'
    },
    { 
      id: 'vivid', 
      name: language === 'KR' ? '💖 생생한 모드' : '💖 Vivid Mode',
      description: language === 'KR' ? '화사하고 밝은 색감' : 'Bright and cheerful colors'
    },
    { 
      id: 'soft', 
      name: language === 'KR' ? '🌙 부드러운 모드' : '🌙 Soft Mode',
      description: language === 'KR' ? '포근하고 은은한 느낌' : 'Gentle and dreamy feel'
    },
    { 
      id: 'outfocus', 
      name: language === 'KR' ? '✨ 아웃포커스 모드' : '✨ Out-Focus Mode',
      description: language === 'KR' ? '로맨틱한 배경 흐림' : 'Romantic background blur'
    },
    { 
      id: 'painting', 
      name: language === 'KR' ? '🎨 그림 모드' : '🎨 Painting Mode',
      description: language === 'KR' ? '예술적인 그림 스타일' : 'Artistic painting style'
    }
  ]);

  const userId = user ? user.uid : 'unknown';
  const isMobileDevice = () => /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  useEffect(() => {
    setPresetOptions([
      { 
        id: 'portrait', 
        name: language === 'KR' ? '✨ 인물 모드' : '✨ Portrait Mode',
        description: language === 'KR' ? '아름다운 인물 사진으로' : 'Beautiful portrait enhancement'
      },
      { 
        id: 'landscape', 
        name: language === 'KR' ? '🌸 풍경 모드' : '🌸 Landscape Mode',
        description: language === 'KR' ? '멋진 풍경 사진으로' : 'Stunning landscape view'
      },
      { 
        id: 'macro', 
        name: language === 'KR' ? '🌺 접사 모드' : '🌺 Macro Mode',
        description: language === 'KR' ? '섬세한 디테일 촬영' : 'Detailed close-up shots'
      },
      { 
        id: 'hdr', 
        name: language === 'KR' ? '🌈 HDR 모드' : '🌈 HDR Mode',
        description: language === 'KR' ? '생생한 색감 표현' : 'Vibrant color expression'
      },
      { 
        id: 'blackwhite', 
        name: language === 'KR' ? '🖤 흑백 모드' : '🖤 Black & White Mode',
        description: language === 'KR' ? '클래식한 흑백 감성' : 'Classic monochrome style'
      },
      { 
        id: 'vintage', 
        name: language === 'KR' ? '📷 빈티지 모드' : '📷 Vintage Mode',
        description: language === 'KR' ? '따뜻한 레트로 감성' : 'Warm retro atmosphere'
      },
      { 
        id: 'vivid', 
        name: language === 'KR' ? '💖 생생한 모드' : '💖 Vivid Mode',
        description: language === 'KR' ? '화사하고 밝은 색감' : 'Bright and cheerful colors'
      },
      { 
        id: 'soft', 
        name: language === 'KR' ? '🌙 부드러운 모드' : '🌙 Soft Mode',
        description: language === 'KR' ? '포근하고 은은한 느낌' : 'Gentle and dreamy feel'
      },
      { 
        id: 'outfocus', 
        name: language === 'KR' ? '✨ 아웃포커스 모드' : '✨ Out-Focus Mode',
        description: language === 'KR' ? '로맨틱한 배경 흐림' : 'Romantic background blur'
      },
      { 
        id: 'painting', 
        name: language === 'KR' ? '🎨 그림 모드' : '🎨 Painting Mode',
        description: language === 'KR' ? '예술적인 그림 스타일' : 'Artistic painting style'
      }
    ]);
  }, [language]);

  useEffect(() => {
    recordPageVisit('ConvertToCanon', {
      userId,
      userIp,
      countryCode,
      SERVER_ROOT,
      language,
      isMobileDevice: isMobileDevice()
    });
  }, []);

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setPreviewUrl(reader.result);
        setOutputImage(null);
      };
      reader.readAsDataURL(file);
      setImageFile(file);
      
      setMessage(
        language === 'KR'
          ? "💫 완벽해요! 이제 원하는 모드를 선택하고 '✨ 변환하기' 버튼을 눌러보세요!"
          : "💫 Perfect! Now choose your favorite mode and click '✨ Transform'!"
      );
    }
  };

  const handleConvertImage = async () => {
    if (!imageFile) {
      alert(language === 'KR' ? '📸 먼저 사진을 올려주세요!' : '📸 Please upload a photo first!');
      return;
    }

    setIsProcessing(true);
    setMessage(language === 'KR' ? '✨ 마법을 부리는 중... 잠시만 기다려주세요!' : '✨ Working magic... Please wait!');

    try {
      const processedImage = await CanonEffectProcessor.processImage(
        imageFile, 
        selectedPreset, 
        paintingStyle,
        artStyle,
        setMessage, 
        language
      );
      
      setOutputImage(processedImage);
      setIsProcessing(false);
      setMessage(
        language === 'KR' 
          ? `🎉 와! ${presetOptions.find(p => p.id === selectedPreset).name.replace(/[✨🌸🌺🌈🖤📷💖🌙🎨]/g, '').trim()}로 완벽하게 변환되었어요!` 
          : `🎉 Amazing! Successfully transformed to ${presetOptions.find(p => p.id === selectedPreset).name.replace(/[✨🌸🌺🌈🖤📷💖🌙🎨]/g, '').trim()}!`
      );
    } catch (error) {
      console.error('Image processing error:', error);
      setIsProcessing(false);
      setMessage(
        language === 'KR' 
          ? '😅 앗, 처리 중에 문제가 생겼어요. 다시 시도해주세요!' 
          : '😅 Oops! Something went wrong. Please try again!'
      );
    }
  };

  const handleDownloadImage = () => {
    if (!outputImage) return;
    
    const link = document.createElement('a');
    link.href = outputImage;
    link.download = `canon_${selectedPreset}_image.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="convert-to-canon">
      <Helmet>
        <title>
          {language === 'KR' 
            ? '✨ iPhone 사진을 Canon 모드로 변환 | Doldari' 
            : '✨ Convert iPhone Photos to Canon Mode | Doldari'}
        </title>
        <link rel="icon" href="/images/thumbnail_canonconverter.png" />
        <meta
          name="description"
          content={language === 'KR' 
            ? '🌸 iPhone으로 촬영한 사진을 Canon 카메라의 다양한 모드로 변환해보세요. 인물, 풍경, 접사, HDR 등 다양한 모드를 제공합니다.' 
            : '🌸 Convert your iPhone photos to various Canon camera modes. Portrait, landscape, macro, HDR, and more.'}
        />
      </Helmet>

      <div className="canon-converter-header">
        <h1>
          {language === 'KR' ? '✨ iPhone 사진을 Canon 모드로 변환' : '✨ Transform iPhone Photos to Canon Style'}
        </h1>
        <p className="instruction-text">
          {language === 'KR'
            ? '🌸 당신의 소중한 순간을 전문 Canon 카메라로 촬영한 것처럼 아름답게 변환해드려요'
            : '🌸 Transform your precious moments to look like they were captured with a professional Canon camera'}
        </p>
      </div>

      

      <div className="canon-converter-main">
        {/* 왼쪽: 컨트롤 영역 */}
        <div className="canon-controls">
          <div className="control-card">
            <div className="upload-section">
              <label htmlFor="image-upload" className="upload-label">
                📸 {language === 'KR' ? '사진 선택하기' : 'Choose Your Photo'}
              </label>
              <div className="upload-button-container">
                <input
                  id="image-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="upload-input"
                />
                <label htmlFor="image-upload" className="upload-button">
                  {imageFile ? 
                    (language === 'KR' ? '✅ 사진이 선택되었어요!' : '✅ Photo Selected!') :
                    (language === 'KR' ? '🌸 사진 올리기' : '🌸 Upload Photo')
                  }
                </label>
              </div>
            </div>

            <PresetSelector
              presets={presetOptions}
              selectedPreset={selectedPreset}
              onChange={(value) => {
                setSelectedPreset(value);
                setShowPaintingOptions(value === 'painting');
              }}
              language={language}
            />

            {showPaintingOptions && (
              <PaintingModeSelector
                paintingStyle={paintingStyle}
                artStyle={artStyle}
                onPaintingStyleChange={setPaintingStyle}
                onArtStyleChange={setArtStyle}
                language={language}
              />
            )}

            <div className="action-buttons">
              <button
                onClick={handleConvertImage}
                className="convert-button"
                disabled={!imageFile || isProcessing}
              >
                {isProcessing ? 
                  (language === 'KR' ? '✨ 변환 중...' : '✨ Transforming...') :
                  (language === 'KR' ? '✨ 변환하기' : '✨ Transform')
                }
              </button>
              
              {outputImage && (
                <button
                  onClick={handleDownloadImage}
                  className="download-button"
                >
                  💾 {language === 'KR' ? '다운로드' : 'Download'}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* 오른쪽: 이미지 프리뷰 영역 */}
        <div className="canon-preview-area">
          <div className="preview-grid">
            <div className="preview-container">
              <div className="preview-header">
                <h3>🌸 {language === 'KR' ? '원본 사진' : 'Original Photo'}</h3>
              </div>
              <div className="image-frame">
                <ImagePreview 
                  url={previewUrl} 
                  altText={language === 'KR' ? '원본 이미지' : 'Original Image'} 
                />
              </div>
            </div>

            <div className="output-container">
              <div className="preview-header">
                <h3>✨ {language === 'KR' ? '변환된 사진' : 'Transformed Photo'}</h3>
              </div>
              <div className="image-frame">
                {isProcessing ? (
                  <ProcessingIndicator language={language} />
                ) : (
                  <ImagePreview 
                    url={outputImage} 
                    altText={language === 'KR' ? '변환된 이미지' : 'Transformed Image'} 
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {message && (
        <div className="message-container">
          <div className="message-card">
            <p className="message">{message}</p>
          </div>
        </div>
      )}

      {/* 단계별 가이드 추가 */}
      <div className="step-guide">
        <div className="step-item">
          <span className="step-number">1</span>
          <span className="step-text">
            {language === 'KR' ? '📸 사진 업로드' : '📸 Upload Photo'}
          </span>
        </div>
        <div className="step-arrow">→</div>
        <div className="step-item">
          <span className="step-number">2</span>
          <span className="step-text">
            {language === 'KR' ? '🎨 모드 선택' : '🎨 Choose Mode'}
          </span>
        </div>
        <div className="step-arrow">→</div>
        <div className="step-item">
          <span className="step-number">3</span>
          <span className="step-text">
            {language === 'KR' ? '✨ 변환하기' : '✨ Transform'}
          </span>
        </div>
      </div>
    </div>
  );
}

export default ConvertToCanon;